// Admin App 根
import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAdminMe } from './hooks/useAdminMe.js'
import { RequireAdmin } from './components/RequireAdmin.js'
import { FEATURES } from './config/features.js'
import '@vivilo/shared/styles/globals.css'
import { DialogHost } from './components/Dialog.js'

const AdminLogin = lazy(() =>
  import('./pages/AdminLogin.js').then((m) => ({ default: m.AdminLoginPage })),
)
const AdminStats = lazy(() =>
  import('./pages/AdminStats.js').then((m) => ({ default: m.AdminStatsPage })),
)
const AdminUsers = lazy(() =>
  import('./pages/AdminUsers.js').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminOrders = lazy(() =>
  import('./pages/AdminOrders.js').then((m) => ({ default: m.AdminOrdersPage })),
)
const AdminTasks = lazy(() =>
  import('./pages/AdminTasks.js').then((m) => ({ default: m.AdminTasksPage })),
)
const AdminPresets = lazy(() =>
  import('./pages/AdminPresets.js').then((m) => ({ default: m.AdminPresetsPage })),
)
const AdminAudit = lazy(() =>
  import('./pages/AdminAudit.js').then((m) => ({ default: m.AdminAuditPage })),
)
const AdminConfig = lazy(() =>
  import('./pages/AdminConfig.js').then((m) => ({ default: m.AdminConfigPage })),
)
const AdminFeedback = lazy(() =>
  import('./pages/AdminFeedback.js').then((m) => ({ default: m.AdminFeedbackPage })),
)
const AdminCreativePerf = lazy(() =>
  import('./pages/AdminCreativePerf.js').then((m) => ({ default: m.AdminCreativePerfPage })),
)
const AdminTopicConfig = lazy(() =>
  import('./pages/AdminTopicConfig.js').then((m) => ({ default: m.AdminTopicConfigPage })),
)
const AdminBillingRecharge = lazy(() =>
  import('./pages/AdminBillingRecharge.js').then((m) => ({ default: m.AdminBillingRechargePage })),
)
const AdminBillingMembership = lazy(() =>
  import('./pages/AdminBillingMembership.js').then((m) => ({ default: m.AdminBillingMembershipPage })),
)
const AdminPaymentsReconcile = lazy(() =>
  import('./pages/AdminPaymentsReconcile.js').then((m) => ({ default: m.AdminPaymentsReconcilePage })),
)
const AdminPaymentsOrphans = lazy(() =>
  import('./pages/AdminPaymentsOrphans.js').then((m) => ({ default: m.AdminPaymentsOrphansPage })),
)
const AdminPromptSpecs = lazy(() =>
  import('./pages/AdminPromptSpecs.js').then((m) => ({ default: m.AdminPromptSpecsPage })),
)
const Manuals = lazy(() => import('./pages/Manuals.js').then((m) => ({ default: m.Manuals })))
const ManualDetail = lazy(() =>
  import('./pages/ManualDetail.js').then((m) => ({ default: m.ManualDetail })),
)
const Candidates = lazy(() =>
  import('./pages/Candidates.js').then((m) => ({ default: m.Candidates })),
)

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
})

function AppShell(): JSX.Element {
  useAdminMe()
  return (
    <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}>加载中…</div>}>
      <Routes>
        <Route path="/" element={<Navigate to="/stats" replace />} />
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/stats" element={<RequireAdmin><AdminStats /></RequireAdmin>} />
        <Route path="/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
        <Route path="/tasks" element={<RequireAdmin><AdminTasks /></RequireAdmin>} />
        <Route path="/audit" element={<RequireAdmin><AdminAudit /></RequireAdmin>} />
        <Route path="/config" element={<RequireAdmin><AdminConfig /></RequireAdmin>} />
        <Route path="/feedback" element={<RequireAdmin><AdminFeedback /></RequireAdmin>} />
        <Route path="/creative-perf" element={<RequireAdmin><AdminCreativePerf /></RequireAdmin>} />
        <Route path="/topic-config" element={<RequireAdmin><AdminTopicConfig /></RequireAdmin>} />
        {/* 商品配置:与支付渠道无关,常开 */}
        {FEATURES.billingConfig && (
          <>
            <Route path="/billing" element={<Navigate to="/billing/recharge" replace />} />
            <Route path="/billing/recharge" element={<RequireAdmin><AdminBillingRecharge /></RequireAdmin>} />
            <Route path="/billing/membership" element={<RequireAdmin><AdminBillingMembership /></RequireAdmin>} />
          </>
        )}
        {FEATURES.payment && <Route path="/orders" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />}
        {/* 人工转账的对账配套:随渠道停用 */}
        {FEATURES.transferPay && (
          <>
            <Route path="/payments/reconcile" element={<RequireAdmin><AdminPaymentsReconcile /></RequireAdmin>} />
            <Route path="/payments/orphans" element={<RequireAdmin><AdminPaymentsOrphans /></RequireAdmin>} />
          </>
        )}
        {FEATURES.contentOps && (
          <>
            {FEATURES.presets && <Route path="/presets" element={<RequireAdmin><AdminPresets /></RequireAdmin>} />}
            <Route path="/prompt-specs" element={<RequireAdmin><AdminPromptSpecs /></RequireAdmin>} />
            <Route path="/manuals" element={<RequireAdmin><Manuals /></RequireAdmin>} />
            <Route path="/manuals/:slug" element={<RequireAdmin><ManualDetail /></RequireAdmin>} />
            <Route path="/candidates" element={<RequireAdmin><Candidates /></RequireAdmin>} />
          </>
        )}
        <Route path="*" element={<Navigate to="/stats" replace />} />
      </Routes>
    </Suspense>
  )
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      <DialogHost />
    </QueryClientProvider>
  )
}
